import React, { useMemo } from "react";
import { useGlobalContext } from "../context/globalContext";
import { useEffect, useState } from "react";
import { Toaster, toast } from "react-hot-toast";
import Loading from "../components/Loading";
import UserOverallStatsCard from "../components/UserOverallStatsCard";
import { useNavigate } from "react-router-dom";

export default function CurrUsersStats() {
  const { userInfo } = useGlobalContext();
  const navigate = useNavigate();
  const [fetchingData, setFetchingData] = useState(false);
  const [allUsersData, setAllUsersData] = useState(null);

  const API_BASE_URL = "https://66z6lhu4ge.execute-api.ap-south-1.amazonaws.com/prod/call";

  const getAllUsersData = async () => {
    setFetchingData(true);
    try {
      // First, get all agents
      const agentsResponse = await fetch(`${API_BASE_URL}/get-agent-data`);
      const agents = await agentsResponse.json();

      if (!agents || !Array.isArray(agents)) {
        throw new Error('Failed to fetch agents data');
      }

      // Get current date and start of month for date range
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
      const today = now.toISOString().split('T')[0];

      // Get data for each agent for the current month
      const allAgentsData = await Promise.all(
        agents.map(async (agent) => {
          const queryParams = new URLSearchParams({
            agent_number: agent.agent_number,
            start_date: startOfMonth,
            end_date: today
          });

          const response = await fetch(`${API_BASE_URL}/get-data?${queryParams}`);
          const data = await response.json();

          // Aggregate all calls data for the month
          const monthStats = data.reduce((acc, record) => {
            acc.totalCallCount = (acc.totalCallCount || 0) + (record.total_calls || 0);
            acc.totalConnectedCount = (acc.totalConnectedCount || 0) + 
              ((record.ic_answered || 0) + (record.oc_answered || 0));
            acc.totalChatCount = (acc.totalChatCount || 0); // If you track chat counts
            acc.totalCount = (acc.totalCount || 0) + (record.total_calls || 0);
            return acc;
          }, {});

          return {
            userName: agent.name,
            agentNumber: agent.agent_number,
            ...monthStats
          };
        })
      );

      // Sort by total connected count
      allAgentsData.sort((a, b) => b.totalConnectedCount - a.totalConnectedCount);
      
      setAllUsersData(allAgentsData);
      toast.success("Data fetched successfully");
    } catch (err) {
      console.error("Error fetching data:", err);
      toast.error("Failed to fetch data");
    } finally {
      setFetchingData(false);
    }
  };

  // Memoize the fetched data
  const memoizedUsersData = useMemo(() => allUsersData, [allUsersData]);

  useEffect(() => {
    if (userInfo?.isAdmin === false) {
      toast.error("You are not authorized to view this page");
      navigate("/");
      return;
    }
    getAllUsersData();
  }, [userInfo?.isAdmin]);

  return (
    <>
      <div className="flex flex-row flex-wrap items-center justify-evenly">
        <h1 className="text-xl font-semibold w-full text-center my-2">
          All agents stats for current month
        </h1>
        {fetchingData ? (
          <Loading />
        ) : (
          <div className="flex flex-row flex-wrap items-center justify-evenly">
            {memoizedUsersData?.map((user, index) => (
              <UserOverallStatsCard
                key={user.agentNumber}
                userData={user}
                index={index}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
}